import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { usePostDocumentUploadMutation } from "../../../../services/api/repositories/DocumentsRepository";
import PostDocumentUploadQuery from "../../../../models/api/Documents/PostDocumentUploadQuery";
import { NavLink, useNavigate } from "react-router-dom";
import ClientInput from "../../mailing-rules/common/form/ClientInput";
import { IClientType } from "../../../../interfaces/api/IClientsType";
import DatePicker from 'react-datepicker';

export default function DocumentCreateSection() {
    const navigate = useNavigate();

    const [postDocumentUpload, { isSuccess, isLoading, isError, error }] = usePostDocumentUploadMutation();
    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ description: string, type: string, file: string, dateStart: string, dateEnd: string }>({ description: "", type: "", file: "", dateStart: "", dateEnd: "" });

    const [clientTypeList, setClientTypeList] = useState<IClientType[]>([]);
    const [clientTypeUuidList, setclientTypeUuidList] = useState<string[]>([]);
    const [selectedType, setSelectedType] = useState<string>("none");

    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(null);

    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const typeRef = useRef<HTMLSelectElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const handleClientTypeAdd = (clientType: IClientType) => {
        if (clientTypeList.some(existingClientType => existingClientType.uuid === clientType.uuid)) {
            return;
        }
        setClientTypeList([...clientTypeList, clientType]);
    };

    const handleClientTypeRemove = (value: string) => {
        setClientTypeList(clientTypeList.filter(item => item.uuid !== value));
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== value));
    }

    const handleClientTypeUuidAdd = (uuid: string) => {
        if (clientTypeUuidList.some(existingUuidClient => existingUuidClient === uuid)) {
            return;
        }
        setclientTypeUuidList([...clientTypeUuidList, uuid]);
    };

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    const handleClose = useCallback(() => {
        setShow(false)
    }, [setShow]);

    const handleShow = useCallback(async () => {
        setShowValidation(true);

        if (!descriptionRef.current?.value || !typeRef.current?.value || !fileRef.current?.files?.[0] || clientTypeList.length === 0) {
            return;
        }

        let errors = validationErrors;
        let canSend = true;

        if (descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            canSend = false;
        }

        if (fileRef.current?.files?.[0]?.type !== "application/pdf") {
            errors.file = "Le fichier doit être un PDF";
            canSend = false;
        }

        const isLegalCgv = typeRef.current?.value === 'legal_cgv';
        const hasNoStartDate = isLegalCgv && !startDate;
        const hasInvalidDateRange = isLegalCgv && startDate && endDate && startDate > endDate;

        if (hasNoStartDate) {
            errors.dateStart = "La date de début est obligatoire";
            canSend = false;
        }

        if (hasInvalidDateRange) {
            errors.dateEnd = "La date de fin doit être postérieure à la date de début";
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true)
    }, [clientTypeList.length, validationErrors, startDate, endDate]);

    const handleSubmit = useCallback(async () => {
        setShowValidation(true);
        if (!descriptionRef.current?.value || !typeRef.current?.value || !fileRef.current?.files?.[0] || clientTypeList.length === 0) {
            return;
        }

        let errors = validationErrors;
        let canSend = true;

        if (descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            canSend = false;
        }

        if (fileRef.current?.files?.[0]?.type !== "application/pdf") {
            errors.file = "Le fichier doit être un PDF";
            canSend = false;
        }

        const isLegalCgv = typeRef.current?.value === 'legal_cgv';
        const hasNoStartDate = isLegalCgv && !startDate;
        const hasInvalidDateRange = isLegalCgv && startDate && endDate && startDate > endDate;

        if (hasNoStartDate) {
            errors.dateStart = "La date de début est obligatoire";
            canSend = false;
        }

        if (hasInvalidDateRange) {
            errors.dateEnd = "La date de fin doit être postérieure à la date de début";
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        let date_validity_start: string | undefined = undefined;
        let date_validity_end: string | undefined = undefined;

        if (typeRef.current?.value === 'legal_cgv') {
            date_validity_start = startDate ? `${startDate.toLocaleDateString()} 00:00:00` : `${new Date().toLocaleDateString()} 00:00:00`;
            date_validity_end = endDate ? `${endDate.toLocaleDateString()} 23:59:59` : undefined;
        }

        postDocumentUpload(
            new PostDocumentUploadQuery({
                description: descriptionRef.current?.value,
                type: typeRef.current?.value,
                file: fileRef.current?.files?.[0],
                client_types: clientTypeUuidList,
                date_validity_start,
                date_validity_end
            })
        );

        setShow(false);
    }, [clientTypeList.length, validationErrors, postDocumentUpload, clientTypeUuidList, startDate, endDate]);

    const handleError = useCallback(() => {
        if (isError && error !== undefined && error !== null && typeof error === 'object' && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {
            if ("error" in error) {
                setErrMsg(error.error as string);
            } else if ("data" in error && typeof error.data === 'object' && error.data !== null && "message" in error.data) {
                setErrMsg(error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [isError, error, errMsg]);

    useEffect(() => {
        if (isSuccess) {
            navigate("/documents");
        }

        if (isError) {
            handleError();
        }

    }, [isSuccess, navigate, isError, handleError]);

    return (
        <>
            <div>
                <div className="object attach">
                    {isLoading && (<h3>Enregistrement en cours</h3>)}
                    {!isLoading && !isSuccess &&
                        <Form className="form" validated={showValidation}>
                            <section className="form-section id">
                                <h3>
                                    <span className="icon"></span>
                                    <span className="text">Définition</span>
                                </h3>
                                <Form.Group className="form-group attach-description">
                                    <Form.Label className="form-label" htmlFor="attach-description-input">Description</Form.Label>
                                    <Form.Control
                                        ref={descriptionRef}
                                        required
                                        as="textarea"
                                        type="text"
                                        className="form-control w100"
                                        id="attach-description-input"
                                        tabIndex={2}
                                        maxLength={255}
                                        isInvalid={validationErrors.description.length > 0}
                                    >
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{validationErrors.description.length > 0 ? validationErrors.description : "Veuillez saisir une description"}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="form-group attach-type required">
                                    <Form.Label className="form-label" htmlFor="attach-type-select">Type</Form.Label>
                                    <Form.Select
                                        ref={typeRef}
                                        required
                                        className="form-select"
                                        id="attach-type-select"
                                        tabIndex={3}
                                        defaultValue="none"
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        isInvalid={typeRef.current?.value === "none" || validationErrors.type.length > 0}
                                    >
                                        <option value="none" disabled>Sélectionner un Type</option>
                                        <option value="legal_cgv">Conditions générales de ventes</option>
                                        <option value="legal_gc">Garanties commerciales</option>
                                        <option value="not_set">Autres</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{validationErrors.type.length > 0 ? validationErrors.type : "Veuillez sélectionner un type"}</Form.Control.Feedback>
                                </Form.Group>

                                <ClientInput
                                    clientTypeList={clientTypeList}
                                    handleClientTypeAdd={handleClientTypeAdd}
                                    handleClientTypeUuidAdd={handleClientTypeUuidAdd}
                                    handleClientTypeRemove={handleClientTypeRemove}
                                />
                            </section>

                            {selectedType === 'legal_cgv' && (
                                <section className="form-section schedule">
                                    <h3><span className="icon"></span><span className="text">Programmation</span></h3>
                                    <div className="fromTo">
                                        <Form.Group className="form-group mailing-runDateStart">
                                            <Form.Label className="form-label" htmlFor="mailing-runDateStart-input">Du</Form.Label>
                                            <DatePicker 
                                                className={`form-control w100 ${validationErrors.dateStart.length > 0 ? 'is-invalid' : ''}`}
                                                id="mailing-runDateStart-input" 
                                                tabIndex={4} 
                                                dateFormat="dd/MM/yyyy"
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                            />
                                            <Form.Control.Feedback type="invalid">{validationErrors.dateStart}</Form.Control.Feedback>
                                        </Form.Group >
                                        <Form.Group className="form-group mailing-runDateEnd">
                                            <Form.Label className="form-label" htmlFor="mailing-runDateEnd-input">Au</Form.Label>
                                            <DatePicker
                                                className={`form-control w100 ${validationErrors.dateEnd.length > 0 ? 'is-invalid' : ''}`}
                                                id="mailing-runDateEnd-input"
                                                tabIndex={5}
                                                dateFormat="dd/MM/yyyy"
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                            />
                                            <Form.Control.Feedback type="invalid">{validationErrors.dateEnd}</Form.Control.Feedback>
                                        </Form.Group >
                                    </div>
                                </section>
                            )}

                            <section className="form-section file">
                                <h3>
                                    <span className="icon"></span>
                                    <span className="text">Fichier</span>
                                </h3>
                                <Form.Group className="form-group attach-file required">
                                    <Form.Label className="form-label" htmlFor="attach-file-input">Fichier</Form.Label>
                                    <Form.Control
                                        ref={fileRef}
                                        required type="file"
                                        className="form-control w100"
                                        id="attach-file-input"
                                        tabIndex={4}
                                        isInvalid={validationErrors.file.length > 0}
                                    />
                                    <Form.Control.Feedback type="invalid">{validationErrors.file.length > 0 ? validationErrors.file : "Veuillez sélectionner un fichier"}</Form.Control.Feedback>
                                </Form.Group>
                            </section>

                            <div>
                                {Object.values(validationErrors).some(error => error.length > 0) && (
                                    <Alert variant="danger" dismissible>
                                        {Object.values(validationErrors)
                                            .filter(error => error.length > 0)
                                            .map((error, index) => (
                                                <div key={index}>{error}</div>
                                            ))}
                                    </Alert>
                                )}

                                {isError && errMsg && (
                                    <Alert variant="danger" dismissible>
                                        {errMsg}
                                    </Alert>
                                )}
                            </div>

                            <div className="form-submit">
                                <NavLink to={"/documents"} type="button" className="btn btn-secondary btn-cancel" tabIndex={6} title="Annuler">
                                    <span className="icon"></span>
                                    <span className="text">Annuler</span>
                                </NavLink>
                                <Button type="button" className="btn btn-primary btn-ok" onClick={() => { handleShow() }} tabIndex={5} title="Ajouter le nouvelle Pièce jointe">
                                    <span className="icon"></span>
                                    <span className="text">Ajouter</span>
                                </Button>
                            </div>
                        </Form>
                    }
                </div>
            </div>
            <SubmitModal 
                show={show} 
                handleClose={handleClose} 
                handleSubmit={handleSubmit} 
                name={fileRef.current?.files?.[0]?.name || ""} 
                startDate={startDate?.toLocaleDateString()}
                endDate={endDate?.toLocaleDateString()}
            />
        </>
    );
}

function SubmitModal({ show, handleClose, handleSubmit, name, startDate, endDate }: { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string, startDate?: string, endDate?: string }) {
    return (
        <Modal id="documentCreateConfirmationModal" show={show} onHide={() => { handleClose() }}>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="documentCreateConfirmationModalLabel">
                    <span className="icon"></span>
                    <span className="text">Création d'une pièce jointe</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez créer une nouvelle pièce jointe&nbsp;:</p>
                <div className="object attach" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                    {startDate && (
                        <p>En exploitation à partir du {startDate} {endDate && `jusqu'au ${endDate}`}</p>
                    )}
                </div>
                <p>Veuillez valider la création de cette Configuration d'envoi.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    className="btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={() => { handleClose() }}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    variant="primary"
                    className="btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={() => { handleSubmit() }}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}